import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  BookOpen,
  Calculator,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  FlaskConical,
  Gem,
  GraduationCap,
  Layers,
  LayoutDashboard,
  Library,
  LibraryBig,
  LogOut,
  Microscope,
  Moon,
  Orbit,
  Play,
  Settings2,
  ShoppingBag,
  Sparkles,
  Sun,
  Users,
  Workflow,
  X,
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useAttemptNav } from '@/hooks/useAttemptNav'
import { useFlashcards } from '@/hooks/useFlashcards'
import { useCollectGlow } from '@/hooks/useCollectGlow'
import { useMobileNav } from '@/hooks/useMobileNav'
import { MobileNavButton } from '@/components/MobileNavButton'
import { pageHostsNavButton } from '@/lib/mobileNavHost'
import { useGems } from '@/hooks/useGems'
import { getDailyQuizStats, DAILY_QUIZ_EVENT } from '@/lib/dailyProgressStore'
import { useSubscription } from '@/hooks/useSubscription'
import { useTheme } from '@/hooks/useTheme'
import ExamsPopout from '@/components/ExamsPopout'
import { AvatarDisplay } from '@/components/AvatarDisplay'
import { ProBadge, NAME_PILL } from '@/components/ProBadge'
import { useExamsPopout } from '@/hooks/useExamsPopout'
import { parseBanner, DESIGNATION_BANNERS } from '@/lib/banners'
import { COWORK_ENABLED, RESEARCH_TAB_ENABLED, TOOLS_ENABLED } from '@/lib/featureFlags'
import { useActuariaAccess } from '@/hooks/useActuariaAccess'
import { useCommunityAccess } from '@/hooks/useCommunityAccess'
import { StatusChip } from '@/components/actuaria/StatusChip'
import { ModeSwitcher } from '@/components/ModeSwitcher'
import { useCoworkLibrary } from '@/hooks/useCoworkLibrary'
import { modeForPath } from '@/lib/appMode'
import { ATTEMPT_VIEW_ICON, ATTEMPT_VIEW_LABEL, attemptRoute } from '@/lib/attemptViews'
import { RESOURCES_ROUTE, studyGuidesSection, type StudyGuidesSection } from '@/lib/wikiRoutes'
import { TodayQuizCornerBadge, TodayQuizNavBadge } from '@/components/TodayQuizBadge'
import { useTodayQuizCounts } from '@/hooks/useTodayQuizCount'
import { SoundPopover } from '@/components/SoundPopover'
import { cn } from '@/lib/utils'

const STORAGE_KEY = 'quiz.sidebar.collapsed'

function getLastWikiPath(): string {
  try { return sessionStorage.getItem('wiki:last-path') || '/wiki' } catch { return '/wiki' }
}

function getInitialCollapsed(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

type ItemProps = {
  to: string
  label: string
  icon: React.ReactNode
  collapsed: boolean
  external?: boolean
  end?: boolean
  onNavigate?: () => void
  badge?: React.ReactNode
  forceActive?: boolean
  dataTour?: string
  dataFlashcardNav?: boolean
  /**
   * Decides the highlight in place of the route match, for a row whose place
   * isn't a pathname — an attempt's view is a search parameter, which a
   * NavLink doesn't match on. `within` is a row one of whose children is the
   * lit one: named, not filled, as a group's heading is.
   */
  state?: 'active' | 'within' | 'idle'
  /** Replace the history entry rather than push one, as switching an attempt's view does. */
  replace?: boolean
}

function SidebarItem({ to, label, icon, collapsed, external, end, onNavigate, badge, forceActive, dataTour, dataFlashcardNav, state, replace }: ItemProps) {
  // Phone-first sizing: below `lg` the drawer *is* the screen, so a row is a
  // full-width 48px target carrying 16px type; at `lg` it narrows back into the
  // 16rem rail at the app's default body size (style guide §3, §11).
  const base =
    'flex items-center gap-3 rounded-lg px-3 py-3 text-base transition-colors lg:rounded-md lg:py-2 lg:text-sm'
  const inactive = 'text-muted-foreground hover:text-foreground hover:bg-accent/60'
  const active = 'bg-card text-foreground font-medium'
  const within = 'text-foreground font-medium hover:bg-accent/60'
  const tone = { active, within, idle: inactive }

  const content = (
    <>
      <span className="flex h-6 w-6 shrink-0 items-center justify-center lg:h-5 lg:w-5">{icon}</span>
      <span className={`flex-1 truncate ${collapsed ? 'lg:hidden' : ''}`}>{label}</span>
      {badge && <span className={`shrink-0 ${collapsed ? 'lg:hidden' : ''}`}>{badge}</span>}
    </>
  )

  if (external) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noreferrer"
        title={collapsed ? label : undefined}
        className={`${base} ${inactive}`}
        onClick={onNavigate}
        data-tour={dataTour}
      >
        {content}
      </a>
    )
  }

  // A plain Link, because a NavLink would mark itself the current page
  // whenever the pathname matches — every view of an attempt at once.
  if (state) {
    return (
      <Link
        to={to}
        replace={replace}
        title={collapsed ? label : undefined}
        aria-current={state === 'active' ? 'page' : undefined}
        className={`${base} ${tone[state]}`}
        onClick={onNavigate}
        data-tour={dataTour}
      >
        {content}
      </Link>
    )
  }

  return (
    <NavLink
      to={to}
      end={end}
      replace={replace}
      title={collapsed ? label : undefined}
      className={({ isActive }) => `${base} ${(isActive || forceActive) ? active : inactive}`}
      onClick={onNavigate}
      data-tour={dataTour}
      data-flashcard-nav={dataFlashcardNav ? '' : undefined}
    >
      {content}
    </NavLink>
  )
}

function SidebarGroup({
  label,
  icon,
  collapsed,
  isActive,
  children,
}: {
  label: string
  icon: React.ReactNode
  collapsed: boolean
  isActive: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(isActive)

  // Auto-expand when navigating to a child route
  useEffect(() => {
    if (isActive) setOpen(true)
  }, [isActive])

  // When sidebar is icon-only (collapsed desktop), render children directly
  if (collapsed) {
    return <>{children}</>
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`w-full flex items-center gap-3 rounded-lg px-3 py-3 text-base transition-colors lg:rounded-md lg:py-2 lg:text-sm ${
          isActive
            ? 'text-foreground font-medium'
            : 'text-muted-foreground hover:text-foreground hover:bg-accent/60'
        }`}
      >
        <span className="flex h-6 w-6 shrink-0 items-center justify-center lg:h-5 lg:w-5">{icon}</span>
        <span className="flex-1 truncate text-left">{label}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 transition-transform duration-200 lg:h-3.5 lg:w-3.5 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="ml-4 mt-0.5 border-l border-border pl-2 space-y-0.5">
          {children}
        </div>
      )}
    </div>
  )
}

/**
 * The nav rows of **Cowork** mode — its shelves, Sources and Resources, and
 * Workflows.
 * The workflow count is deliberately not a badge here: reading the workflow
 * store would pull the tools engine into the main bundle with the sidebar.
 * Study's rows are not hidden behind a flag here; they are simply not this
 * mode's rows (see `lib/appMode.ts`).
 */
function CoworkNav({ collapsed, onNavigate }: { collapsed: boolean; onNavigate: () => void }) {
  const followed = useCoworkLibrary(s => s.entityIds.length)
  const taken = useCoworkLibrary(s => s.resourceIds.length)
  // A source has its own page under /cowork/sources, and the nav is the only
  // thing saying which of Cowork's places you are in (the page carries no
  // tabs), so the Sources row has to stay lit while you are reading one.
  const { pathname } = useLocation()
  const onSourcePage = pathname.startsWith('/cowork/sources')
  const countBadge = (n: number) =>
    n > 0 ? (
      <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-muted-foreground">
        {n}
      </span>
    ) : undefined

  return (
    <>
      <SidebarItem
        to="/cowork"
        label="Sources"
        icon={<Library className="h-5 w-5 lg:h-4 lg:w-4" />}
        collapsed={collapsed}
        end
        forceActive={onSourcePage}
        onNavigate={onNavigate}
        badge={countBadge(followed)}
      />
      <SidebarItem
        to="/cowork/resources"
        label="Resources"
        icon={<LibraryBig className="h-5 w-5 lg:h-4 lg:w-4" />}
        collapsed={collapsed}
        onNavigate={onNavigate}
        badge={countBadge(taken)}
      />
      <SidebarItem
        to="/cowork/workflows"
        label="Workflows"
        icon={<Workflow className="h-5 w-5 lg:h-4 lg:w-4" />}
        collapsed={collapsed}
        onNavigate={onNavigate}
      />
    </>
  )
}

/**
 * The rows a tab lists under itself while it is open — an attempt's views
 * under Projects, the Study Guides pages under Study Guides — indented behind
 * a rule, so they read as part of the row above.
 */
function SubRows({ collapsed, children }: { collapsed: boolean; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'ml-4 mt-0.5 space-y-0.5 border-l border-border pl-2',
        // The icon-only rail has no room to indent: the rows stand in line under the tab's icon.
        collapsed && 'lg:ml-0 lg:border-l-0 lg:pl-0',
      )}
    >
      {children}
    </div>
  )
}

const STUDY_GUIDE_PAGES: { id: StudyGuidesSection; to: string; label: string; Icon: typeof BookOpen }[] = [
  { id: 'exams', to: '/wiki', label: 'Exams', Icon: GraduationCap },
  { id: 'resources', to: RESOURCES_ROUTE, label: 'Resources', Icon: LibraryBig },
]

/**
 * The Study Guides row and, while the tab is open, its pages under it —
 * **Exams** (the ladder, with the exam and concept pages read from it) and
 * **Resources** (the shelf, with the resource pages opened from it) — drawn
 * the way `ProjectsNav` draws an open attempt's views: the page showing is
 * filled, the tab's own row is named but not filled. Which page a path is
 * under is `studyGuidesSection` (`lib/wikiRoutes.ts`).
 */
function StudyGuidesNav({ collapsed, onNavigate }: { collapsed: boolean; onNavigate: () => void }) {
  const { pathname } = useLocation()
  const section = studyGuidesSection(pathname)
  const iconClass = 'h-5 w-5 lg:h-4 lg:w-4'

  return (
    <>
      <SidebarItem
        to={getLastWikiPath()}
        label="Study Guides"
        icon={<BookOpen className={iconClass} />}
        collapsed={collapsed}
        onNavigate={onNavigate}
        state={section ? 'within' : 'idle'}
      />
      {section && (
        <SubRows collapsed={collapsed}>
          {STUDY_GUIDE_PAGES.map(({ id, to, label, Icon }) => (
            <SidebarItem
              key={id}
              to={to}
              label={label}
              icon={<Icon className={iconClass} />}
              collapsed={collapsed}
              onNavigate={onNavigate}
              state={id === section ? 'active' : 'idle'}
            />
          ))}
        </SubRows>
      )}
    </>
  )
}

/**
 * The Projects row — a Pro feature, so it carries the Pro chip for every
 * reader — and, while an attempt is open, that attempt's views under
 * it — Brief, Workspace, Report, Submit (Results once submitted) — mirroring
 * the switcher in the attempt's top bar, which the attempt page publishes to
 * `useAttemptNav`. Choosing one replaces the history entry, as the switcher
 * does: the views are windows of one project, not pages of it.
 */
function ProjectsNav({ collapsed, onNavigate }: { collapsed: boolean; onNavigate: () => void }) {
  const attempt = useAttemptNav(s => s.current)
  const iconClass = 'h-5 w-5 lg:h-4 lg:w-4'

  return (
    <>
      <SidebarItem
        to="/project"
        label="Projects"
        icon={<FlaskConical className={iconClass} />}
        collapsed={collapsed}
        onNavigate={onNavigate}
        state={attempt ? 'within' : undefined}
        badge={<ProBadge />}
      />
      {attempt && (
        <SubRows collapsed={collapsed}>
          {attempt.views.map(view => {
            const Icon = ATTEMPT_VIEW_ICON[view]
            return (
              <SidebarItem
                key={view}
                to={attemptRoute(attempt.attemptId, view)}
                label={ATTEMPT_VIEW_LABEL[view]}
                icon={<Icon className={iconClass} />}
                collapsed={collapsed}
                onNavigate={onNavigate}
                state={view === attempt.view ? 'active' : 'idle'}
                replace
              />
            )
          })}
        </SubRows>
      )}
    </>
  )
}

/**
 * The **PLAY** group: Actuaria Online, under a label of its own below the study
 * tabs (docs/actuaria-online.md §5). Actuaria is part of Study Mode — a way of
 * studying — not a third product on the mode pill (D6). Its `live` chip is
 * Actuaria's signal, so it carries the `.actuaria` class that defines it.
 */
function PlayNav({ collapsed, onNavigate }: { collapsed: boolean; onNavigate: () => void }) {
  return (
    <>
      <div role="separator" className="!my-3 border-t border-border" />
      <p
        className={cn(
          'px-3 pb-1 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground',
          collapsed && 'lg:hidden',
        )}
      >
        Play
      </p>
      <SidebarItem
        to="/actuaria"
        label="Actuaria"
        icon={<Orbit className="h-5 w-5 lg:h-4 lg:w-4" />}
        collapsed={collapsed}
        onNavigate={onNavigate}
        badge={
          <span className="actuaria">
            <StatusChip variant="live" size="sm">Live</StatusChip>
          </span>
        }
      />
    </>
  )
}

export default function Sidebar() {
  const { user, signOut } = useAuth()
  const { balance: gemBalance } = useGems()
  const { isPro, isBetaTester } = useSubscription()
  // Actuaria is open to approved accounts alone (lib/actuaria/access.ts): no
  // one else sees its row.
  const { allowed: showActuaria } = useActuariaAccess()
  const showCommunity = useCommunityAccess()
  const { theme, toggleTheme } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const { cards } = useFlashcards()
  // Which product the current route belongs to. In Cowork the nav is Cowork's
  // own rows — the two modes are places, not tabs of each other — while the
  // footer (theme, sound, account) stays put because it belongs to neither.
  const appMode = modeForPath(location.pathname)
  const { total: todayQuizTotal, allComplete: todayQuizAllComplete } = useTodayQuizCounts()
  const [dailyQuizStats, setDailyQuizStats] = useState(() => getDailyQuizStats())
  // Lights up the Flashcards item whenever a card is collected.
  const collectGlow = useCollectGlow()

  // Keep daily quiz stats fresh after each quiz completion.
  useEffect(() => {
    const refresh = () => setDailyQuizStats(getDailyQuizStats())
    window.addEventListener(DAILY_QUIZ_EVENT, refresh)
    window.addEventListener('quiz-session-saved', refresh)
    return () => {
      window.removeEventListener(DAILY_QUIZ_EVENT, refresh)
      window.removeEventListener('quiz-session-saved', refresh)
    }
  }, [])

  // Re-keyed on every collect so the pop+glow animation replays each time the
  // count ticks up, mirroring the gem-balance celebration below.
  const flashcardBadge = useMemo(() =>
    cards.length > 0 ? (
      <span
        key={collectGlow}
        className={`text-xs font-semibold px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground tabular-nums lg:text-[10px] ${collectGlow > 0 ? 'flashcard-badge-pop' : ''}`}
      >
        {cards.length}
      </span>
    ) : null,
  [cards.length, collectGlow])

  // Questions left in today's plan (orange, same badge the Quiz tab and the
  // Dashboard's Start button carry) alongside today's answered tally.
  const quizBadge = useMemo(() => {
    if (todayQuizTotal <= 0 && !todayQuizAllComplete && dailyQuizStats.total <= 0) return null
    return (
      <span className="flex items-center gap-1.5">
        <TodayQuizNavBadge count={todayQuizTotal} complete={todayQuizAllComplete} />
        {dailyQuizStats.total > 0 && (
          <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground tabular-nums lg:text-[10px]">
            {dailyQuizStats.correct}/{dailyQuizStats.total}
          </span>
        )}
      </span>
    )
  }, [todayQuizTotal, todayQuizAllComplete, dailyQuizStats.correct, dailyQuizStats.total])

  const [collapsed, setCollapsed] = useState<boolean>(getInitialCollapsed)
  const { open: mobileOpen, closeNav } = useMobileNav()
  // This route pins a bar of its own to the top of the viewport and carries the
  // hamburger on it, so the app header below would be a second row of chrome.
  const navHostedByPage = pageHostsNavButton(location.pathname)
  const [profileOpen, setProfileOpen] = useState(false)
  const { open: examsOpen, openExams, closeExams } = useExamsPopout()
  const [signOutConfirm, setSignOutConfirm] = useState(false)
  const profileRef = useRef<HTMLDivElement>(null)

  // Gem animation: tracks unseen increases and fires when the badge is visible.
  const prevGemBalance = useRef<number | null>(null)
  const pendingGemAnim = useRef(false)
  const [gemAnimKey, setGemAnimKey] = useState(0)

  const fireGemAnim = () => {
    if (!pendingGemAnim.current) return
    pendingGemAnim.current = false
    setGemAnimKey(k => k + 1)
  }

  // Mark pending when balance grows (runs before the visibility effects below).
  useEffect(() => {
    if (prevGemBalance.current !== null && gemBalance > prevGemBalance.current) {
      pendingGemAnim.current = true
    }
    prevGemBalance.current = gemBalance
  }, [gemBalance])

  // Fire immediately if the desktop gem badge is already visible.
  useEffect(() => {
    if (!collapsed) fireGemAnim()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gemBalance, collapsed])

  // Fire when the mobile drawer opens and a balance increase was missed.
  useEffect(() => {
    if (mobileOpen) fireGemAnim()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileOpen])

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0')
    } catch {
      /* ignore */
    }
  }, [collapsed])

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--sidebar-width',
      collapsed ? '3.5rem' : '16rem'
    )
  }, [collapsed])

  useEffect(() => {
    if (!profileOpen) {
      setSignOutConfirm(false)
      return
    }
    function handleClickOutside(e: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [profileOpen])

  const closeMobile = closeNav

  const touchStartX = useRef<number | null>(null)

  const profileName =
    (user?.user_metadata?.full_name as string | undefined) ||
    (user?.user_metadata?.display_name as string | undefined) ||
    user?.email?.split('@')[0] ||
    'Profile'

  const avatarUrl = (user?.user_metadata?.avatar_url as string | undefined) ?? ''
  const profileInitials = profileName.slice(0, 2).toUpperCase()

  const equippedBanner = parseBanner(user?.user_metadata?.banner_data as string | undefined)
  const designationBanner = DESIGNATION_BANNERS.find(b => b.id === equippedBanner?.id)
  const bannerLabel = equippedBanner?.id === 'custom'
    ? (equippedBanner.text ?? '').slice(0, 10) || null
    : equippedBanner?.id === 'beta_tester'
    ? 'Beta'
    : equippedBanner
    ? equippedBanner.id.toUpperCase()
    : null

  return (
    <>
      {/* Below lg: the app header — the hamburger and the wordmark. It is the row a page gets when it pins nothing
          to the top of the viewport itself; a page with a floating search bar
          puts the hamburger on that bar's line instead and this header is not
          rendered at all, so the phone spends one 3.5rem row on chrome rather
          than two. `lib/mobileNavHost.ts` is the one place that says which is
          which, and `App.tsx` reserves the room from the same call. */}
      {!navHostedByPage && (
        <header className="paper-chrome-header fixed top-0 left-0 right-0 h-14 z-30 flex items-center gap-2 px-3 bg-background border-b lg:hidden">
          <MobileNavButton className="-ml-0.5" />
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <Link
              to="/dashboard"
              className="flex items-center gap-1.5 font-semibold text-foreground text-base min-w-0"
            >
              <img src="/favicon.png" alt="" className="h-5 w-5 shrink-0 brightness-0 dark:invert" />
              <span className="truncate">Actuarial Notes</span>
            </Link>
            {COWORK_ENABLED && <ModeSwitcher mode={appMode} onNavigate={closeMobile} />}
          </div>
        </header>
      )}

      {/* Mobile: backdrop — z-[55] so it covers the sticky search bar (z-50) */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-[55] bg-black/50 lg:hidden"
          onClick={closeMobile}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX }}
        onTouchEnd={(e) => {
          if (touchStartX.current === null) return
          if (e.changedTouches[0].clientX - touchStartX.current < -60) closeMobile()
          touchStartX.current = null
        }}
        className={[
          // `paper-chrome-rail` keeps the desktop rail still while the page
          // beside it slides (index.css, "Paper on a desk").
          'paper-chrome-rail flex flex-col bg-background border-r',
          // Mobile: fixed full-width overlay, slides in/out — z-[60] so it sits
          // above the sticky wiki search bar (z-50) and the backdrop (z-[55])
          'fixed inset-y-0 left-0 z-[60] w-full',
          'transition-transform duration-300',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          // Desktop: sticky sidebar in document flow
          'lg:relative lg:inset-auto lg:translate-x-0 lg:z-auto',
          'lg:sticky lg:top-0 lg:h-screen lg:shrink-0',
          collapsed ? 'lg:w-14' : 'lg:w-64',
          'lg:transition-[width] lg:duration-200',
        ].join(' ')}
      >
        {/* Header */}
        <div className="flex items-center h-14 border-b px-3 gap-2">
          <div className={`flex items-center gap-1.5 flex-1 min-w-0 ${collapsed ? 'lg:hidden' : ''}`}>
            <Link
              to="/dashboard"
              onClick={closeMobile}
              className="flex min-w-0 items-center gap-1.5 truncate font-semibold text-foreground transition-colors hover:text-primary"
            >
              <img src="/favicon.png" alt="" className="h-5 w-5 shrink-0 brightness-0 dark:invert" />
              Actuarial Notes
            </Link>
            {COWORK_ENABLED && <ModeSwitcher mode={appMode} onNavigate={closeMobile} />}
          </div>
          {/* Desktop: collapse/expand toggle */}
          <button
            type="button"
            onClick={() => setCollapsed(v => !v)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={`hidden lg:flex h-8 w-8 shrink-0 items-center justify-center rounded-md hover:bg-accent hover:text-accent-foreground transition-colors text-muted-foreground ${collapsed ? 'mx-auto' : 'ml-auto'}`}
          >
            {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
          </button>
          {/* Mobile: close button */}
          <button
            type="button"
            onClick={closeMobile}
            aria-label="Close navigation"
            className="lg:hidden ml-auto h-10 w-10 flex items-center justify-center rounded-lg hover:bg-accent hover:text-accent-foreground transition-colors text-muted-foreground"
          >
            <X className="h-5 w-5 lg:h-4 lg:w-4" />
          </button>
        </div>

        <nav aria-label="Main" className="flex-1 overflow-y-auto px-3 py-3 space-y-1 lg:px-2">
          {appMode === 'cowork' ? (
            <CoworkNav collapsed={collapsed} onNavigate={closeMobile} />
          ) : (
            <>

          {user && (
            <SidebarItem
              to="/dashboard"
              label="Dashboard"
              icon={<LayoutDashboard className="h-5 w-5 lg:h-4 lg:w-4" />}
              collapsed={collapsed}
              onNavigate={closeMobile}
            />
          )}
          {RESEARCH_TAB_ENABLED ? (
            <SidebarGroup
              label="Study Guides"
              icon={<BookOpen className="h-5 w-5 lg:h-4 lg:w-4" />}
              collapsed={collapsed}
              isActive={
                location.pathname.startsWith('/wiki') ||
                location.pathname.startsWith('/research')
              }
            >
              <SidebarItem
                to="/wiki"
                label="Actuarial Exams"
                icon={<GraduationCap className="h-5 w-5 lg:h-4 lg:w-4" />}
                collapsed={collapsed}
                end
                onNavigate={closeMobile}
                forceActive={studyGuidesSection(location.pathname) === 'exams'}
              />
              <SidebarItem
                to={RESOURCES_ROUTE}
                label="Resources"
                icon={<LibraryBig className="h-5 w-5 lg:h-4 lg:w-4" />}
                collapsed={collapsed}
                onNavigate={closeMobile}
                forceActive={studyGuidesSection(location.pathname) === 'resources'}
              />
              {user && (
                <SidebarItem
                  to="/research"
                  label="Research"
                  icon={<Microscope className="h-5 w-5 lg:h-4 lg:w-4" />}
                  collapsed={collapsed}
                  onNavigate={closeMobile}
                  badge={
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 leading-none">
                      Beta
                    </span>
                  }
                />
              )}
            </SidebarGroup>
          ) : (
            <StudyGuidesNav collapsed={collapsed} onNavigate={closeMobile} />
          )}
          <SidebarItem
            to="/flashcards"
            label="Flashcards"
            icon={
              <span className="relative inline-flex items-center justify-center">
                {collectGlow > 0 && <span key={`ring-${collectGlow}`} className="flashcard-nav-ring" aria-hidden="true" />}
                <Layers key={`icon-${collectGlow}`} className={`h-5 w-5 lg:h-4 lg:w-4 ${collectGlow > 0 ? 'flashcard-nav-glow' : ''}`} />
                {/* Collapsed sidebar hides the row badge, so mirror the count as a
                    corner badge on the icon itself when icon-only. */}
                {collapsed && cards.length > 0 && (
                  <span
                    key={`count-${collectGlow}`}
                    className={`hidden lg:flex absolute -top-1.5 -right-2 h-3.5 min-w-[0.875rem] items-center justify-center rounded-full bg-primary px-[3px] text-[9px] font-bold leading-none text-primary-foreground tabular-nums ${collectGlow > 0 ? 'flashcard-badge-pop' : ''}`}
                  >
                    {cards.length}
                  </span>
                )}
              </span>
            }
            collapsed={collapsed}
            onNavigate={closeMobile}
            badge={flashcardBadge}
            dataFlashcardNav
          />
          <SidebarItem
            to="/"
            label="Quiz"
            icon={
              <span className="relative inline-flex items-center justify-center">
                <Play className="h-5 w-5 lg:h-4 lg:w-4" />
                {/* Collapsed sidebar hides the row badge, so mirror the
                    questions-left count as a corner badge on the icon. */}
                {collapsed && (
                  <span className="hidden lg:block">
                    <TodayQuizCornerBadge count={todayQuizTotal} complete={todayQuizAllComplete} size="sm" className="-top-1.5 -right-2" />
                  </span>
                )}
              </span>
            }
            collapsed={collapsed}
            end
            // Games — Quiz Battle and Concept Craft — are ways of quizzing, opened from this tab.
            forceActive={location.pathname === '/games' || location.pathname === '/battle' || location.pathname === '/craft' || location.pathname.startsWith('/craft/')}
            onNavigate={closeMobile}
            badge={quizBadge}
            dataTour="nav-quiz"
          />
          {/* A project is its own kind of study — a brief, a data set and a
              deadline, not a bank of questions — so it is a tab of its own
              (docs/pcpa-project.md). */}
          <ProjectsNav collapsed={collapsed} onNavigate={closeMobile} />
          {/* Calculators that each do one thing — the same engine an AI agent
              runs through the connector (docs/actuarial-tools.md). */}
          {TOOLS_ENABLED && (
            <SidebarItem
              to="/tools"
              label="Tools"
              icon={<Calculator className="h-5 w-5 lg:h-4 lg:w-4" />}
              collapsed={collapsed}
              onNavigate={closeMobile}
            />
          )}
          {/* Real products — registration, study materials, calculators and
              textbooks — each bought on its seller's own page (docs/store.md).
              The gem shop is a corner of it, so this row stays lit there. */}
          <SidebarItem
            to="/store"
            label="Shop"
            icon={<ShoppingBag className="h-5 w-5 lg:h-4 lg:w-4" />}
            collapsed={collapsed}
            onNavigate={closeMobile}
          />
          {/* Where actuaries gather — forums, events and places, by topic
              (docs/community.md). Approved accounts only while in Preview. */}
          {showCommunity && (
            <SidebarItem
              to="/community"
              label="Community"
              icon={<Users className="h-5 w-5 lg:h-4 lg:w-4" />}
              collapsed={collapsed}
              onNavigate={closeMobile}
            />
          )}
          {showActuaria && <PlayNav collapsed={collapsed} onNavigate={closeMobile} />}
            </>
          )}
        </nav>

        <div className="border-t px-3 py-3 space-y-1 lg:px-2">
          <div className={cn('flex items-stretch gap-1', collapsed && 'lg:flex-col')}>
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              title={collapsed ? (theme === 'dark' ? 'Light mode' : 'Dark mode') : undefined}
              className="flex-1 flex items-center gap-3 rounded-lg px-3 py-3 text-base text-muted-foreground hover:text-foreground hover:bg-accent/60 transition-colors lg:rounded-md lg:py-2 lg:text-sm"
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center lg:h-5 lg:w-5">
                {theme === 'dark' ? <Sun className="h-5 w-5 lg:h-4 lg:w-4" /> : <Moon className="h-5 w-5 lg:h-4 lg:w-4" />}
              </span>
              <span className={`truncate ${collapsed ? 'lg:hidden' : ''}`}>
                {theme === 'dark' ? 'Light mode' : 'Dark mode'}
              </span>
            </button>

            {/* Inline sound popout: mute / volume without leaving for Settings */}
            <SoundPopover collapsed={collapsed} />
          </div>

          {user ? (
            <div ref={profileRef} className="relative">
              {profileOpen && (
                <div className="absolute bottom-full left-0 right-0 mb-1 rounded-md border bg-popover shadow-md py-1 z-50">
                  <button
                    type="button"
                    onClick={() => { navigate('/store/gems'); setProfileOpen(false); closeMobile() }}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-base text-foreground hover:bg-accent/60 transition-colors lg:gap-2 lg:py-2 lg:text-sm"
                  >
                    <ShoppingBag className="h-5 w-5 shrink-0 lg:h-4 lg:w-4" />
                    <span>Gem Shop</span>
                    <span
                      key={gemAnimKey}
                      className={`inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400 ${gemAnimKey > 0 ? 'gem-celebrate' : ''}`}
                      aria-label={`${gemBalance} gems`}
                    >
                      <Gem className="h-3 w-3" />
                      {gemBalance}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => { openExams(); setProfileOpen(false); closeMobile() }}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-base text-foreground hover:bg-accent/60 transition-colors lg:gap-2 lg:py-2 lg:text-sm"
                  >
                    <GraduationCap className="h-5 w-5 shrink-0 lg:h-4 lg:w-4" />
                    <span>Exams</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => { navigate('/settings'); setProfileOpen(false); closeMobile() }}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-base text-foreground hover:bg-accent/60 transition-colors lg:gap-2 lg:py-2 lg:text-sm"
                  >
                    <Settings2 className="h-5 w-5 shrink-0 lg:h-4 lg:w-4" />
                    <span>Settings</span>
                  </button>
                  {!isPro && (
                    <button
                      type="button"
                      onClick={() => { navigate('/upgrade'); setProfileOpen(false); closeMobile() }}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 text-base font-medium text-amber-600 dark:text-amber-400 hover:bg-amber-500/10 transition-colors lg:gap-2 lg:py-2 lg:text-sm"
                    >
                      <Sparkles className="h-5 w-5 shrink-0 lg:h-4 lg:w-4" />
                      <span>Upgrade to Pro</span>
                    </button>
                  )}
                  {signOutConfirm ? (
                    <div className="px-3 py-2 space-y-2">
                      <p className="text-xs text-muted-foreground">Sign out?</p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => { signOut(); setProfileOpen(false); setSignOutConfirm(false); closeMobile() }}
                          className="flex-1 rounded-md bg-destructive text-destructive-foreground text-xs py-1.5 font-medium hover:bg-destructive/90 transition-colors"
                        >
                          Sign out
                        </button>
                        <button
                          type="button"
                          onClick={() => setSignOutConfirm(false)}
                          className="flex-1 rounded-md border bg-background text-xs py-1.5 font-medium hover:bg-accent transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                  <button
                    type="button"
                    onClick={() => setSignOutConfirm(true)}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-base text-foreground hover:bg-accent/60 transition-colors lg:gap-2 lg:py-2 lg:text-sm"
                  >
                    <LogOut className="h-5 w-5 shrink-0 lg:h-4 lg:w-4" />
                    <span>Sign out</span>
                  </button>
                  )}
                </div>
              )}
              <button
                type="button"
                onClick={() => setProfileOpen(v => !v)}
                title={collapsed ? profileName : undefined}
                className="w-full flex items-center gap-3 rounded-lg px-3 py-3 text-base text-muted-foreground hover:text-foreground hover:bg-accent/60 transition-colors lg:rounded-md lg:py-2 lg:text-sm"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center lg:h-5 lg:w-5">
                  <AvatarDisplay avatarUrl={avatarUrl} initials={profileInitials} size={20} />
                </span>
                <span className={`flex items-center gap-1.5 min-w-0 ${collapsed ? 'lg:hidden' : ''}`}>
                  <span className="truncate">{profileName}</span>
                  {bannerLabel && (
                    <span
                      title={designationBanner?.fullName}
                      className={cn(
                        NAME_PILL,
                        designationBanner
                          ? 'designation-foil text-foreground'
                          : equippedBanner?.id === 'beta_tester'
                          ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                          : 'bg-violet-500/15 text-violet-700 dark:text-violet-300',
                      )}
                    >
                      {bannerLabel}
                    </span>
                  )}
                  {isPro && (
                    <ProBadge title={isBetaTester ? 'Beta Tester' : undefined} />
                  )}
                </span>
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => { navigate('/settings'); closeMobile() }}
              title={collapsed ? 'You' : undefined}
              className="w-full flex items-center gap-3 rounded-lg px-3 py-3 text-base text-muted-foreground hover:text-foreground hover:bg-accent/60 transition-colors lg:rounded-md lg:py-2 lg:text-sm"
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center lg:h-5 lg:w-5">
                <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5 lg:h-4 lg:w-4">
                  <circle cx="10" cy="10" r="8.25" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3.5 2.5" />
                </svg>
              </span>
              <span className={`truncate ${collapsed ? 'lg:hidden' : ''}`}>You</span>
            </button>
          )}
        </div>
      </aside>

      <ExamsPopout open={examsOpen} onClose={closeExams} />
    </>
  )
}
